import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trash2, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Cart = () => {
    const [cart, setCart] = useState({ items: [] });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCart();
    }, []);

    const fetchCart = async () => {
        try {
            const response = await axios.get(`${API}/cart`);
            setCart(response.data);
        } catch (error) {
            console.error('Error fetching cart:', error);
            toast.error('Failed to load cart');
        } finally {
            setLoading(false);
        }
    };

    const removeFromCart = async (productId) => {
        try {
            await axios.delete(`${API}/cart/${productId}`);
            toast.success('Removed from cart');
            fetchCart();
        } catch (error) {
            toast.error('Failed to remove from cart');
        }
    };

    const calculateTotal = () => {
        return cart.items.reduce((total, item) => {
            return total + (item.product.price * item.quantity);
        }, 0).toFixed(2);
    };

    const handleCheckout = () => {
        navigate('/checkout');
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center pt-16">
                <div className="text-xl text-muted-foreground">Loading...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen pt-24 px-6 md:px-12 lg:px-24 py-12" data-testid="cart-page">
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-12">Shopping Cart</h1>

            {cart.items.length === 0 ? (
                <div className="text-center py-24" data-testid="empty-cart">
                    <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-muted-foreground" strokeWidth={1.5} />
                    <p className="text-xl text-muted-foreground mb-6">Your cart is empty</p>
                    <button 
                        onClick={() => navigate('/browse')}
                        className="btn-primary"
                        data-testid="browse-products-button"
                    >
                        Browse Products
                    </button>
                </div>
            ) : (
                <div className="grid lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-2 space-y-6" data-testid="cart-items">
                        {cart.items.map((item) => (
                            <div 
                                key={item.product.id} 
                                className="bg-card border border-border p-6 flex gap-6"
                                data-testid={`cart-item-${item.product.id}`}
                            >
                                <img 
                                    src={item.product.image_url} 
                                    alt={item.product.name}
                                    className="w-24 h-24 object-cover"
                                />
                                <div className="flex-1">
                                    <h3 className="font-semibold text-lg mb-1">{item.product.name}</h3>
                                    <p className="text-sm text-muted-foreground mb-2">{item.product.description}</p>
                                    <div className="flex items-center justify-between">
                                        <span className="text-xl font-bold text-primary">${item.product.price}</span>
                                        <button 
                                            onClick={() => removeFromCart(item.product.id)}
                                            className="p-2 hover:bg-destructive/10 text-destructive rounded-full transition-colors"
                                            data-testid={`remove-item-${item.product.id}`}
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="lg:col-span-1">
                        <div className="bg-card border border-border p-6 sticky top-24" data-testid="cart-summary">
                            <h3 className="text-2xl font-semibold mb-6">Order Summary</h3>
                            <div className="space-y-4 mb-6">
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Subtotal</span>
                                    <span data-testid="subtotal">${calculateTotal()}</span>
                                </div>
                                <div className="flex justify-between text-muted-foreground">
                                    <span>Tax</span>
                                    <span>$0.00</span>
                                </div>
                                <div className="border-t border-border pt-4">
                                    <div className="flex justify-between text-xl font-bold">
                                        <span>Total</span>
                                        <span className="text-primary" data-testid="total">${calculateTotal()}</span>
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={handleCheckout}
                                className="btn-primary w-full"
                                data-testid="checkout-button"
                            >
                                Proceed to Checkout
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cart;
